export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  PAST_DUE = 'past_due',
  UNPAID = 'unpaid',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  TRIALING = 'trialing',
  ENDED = 'ended', // Custom status for ended subscriptions
  ALL = 'all', // For filtering purposes
}

export interface SubscriptionItem {
  id: string;
  priceId: string;
  quantity: number;
  metadata?: Record<string, string>;
}

export interface Subscription {
  id: string;
  customerId: string;
  customer_id?: string; // For compatibility
  status: SubscriptionStatus;
  items?: SubscriptionItem[];
  price_id?: string; // For single-item subscriptions
  currentPeriodStart: Date;
  current_period_start?: Date; // For compatibility
  currentPeriodEnd: Date;
  current_period_end?: Date; // For compatibility
  cancelAtPeriodEnd: boolean;
  cancel_at_period_end?: boolean; // For compatibility
  trialEnd?: Date;
  trial_end?: Date; // For compatibility
  trialStart?: Date;
  trial_start?: Date; // For compatibility
  canceledAt?: Date;
  canceled_at?: Date; // For compatibility
  endedAt?: Date;
  metadata?: Record<string, string>;
  created: Date;
  updated?: Date;
  // Additional fields for analytics
  amount?: number;
  currency?: string;
  interval?: string;
  interval_unit?: string;
  planName?: string;
  planId?: string;
}

export interface SubscriptionCreateParams {
  customerId: string;
  items?: { priceId: string; quantity?: number }[];
  priceId?: string; // For single-item subscriptions
  trialPeriodDays?: number;
  metadata?: Record<string, string>;
  paymentBehavior?:
    | 'default_incomplete'
    | 'error_if_incomplete'
    | 'allow_incomplete';
  // Additional fields for multi-provider support
  customerEmail?: string;
  customerName?: string;
  successUrl?: string;
  cancelUrl?: string;
}

export interface SubscriptionUpdateParams {
  items?: { priceId: string; quantity?: number }[];
  priceId?: string; // For single-item subscriptions
  quantity?: number;
  cancelAtPeriodEnd?: boolean;
  metadata?: Record<string, string>;
}

// More specific status type unions for better TypeScript support
export type ActiveSubscriptionStatus =
  | SubscriptionStatus.ACTIVE
  | SubscriptionStatus.TRIALING;
export type InactiveSubscriptionStatus =
  | SubscriptionStatus.CANCELED
  | SubscriptionStatus.ENDED;
export type ProblematicSubscriptionStatus =
  | SubscriptionStatus.PAST_DUE
  | SubscriptionStatus.UNPAID;
export type IncompleteSubscriptionStatus =
  | SubscriptionStatus.INCOMPLETE
  | SubscriptionStatus.INCOMPLETE_EXPIRED;

// Status groups for filtering and UI logic
export const ACTIVE_STATUSES: ActiveSubscriptionStatus[] = [
  SubscriptionStatus.ACTIVE,
  SubscriptionStatus.TRIALING,
];

export const INACTIVE_STATUSES: InactiveSubscriptionStatus[] = [
  SubscriptionStatus.CANCELED,
  SubscriptionStatus.ENDED,
];

export const PROBLEMATIC_STATUSES: ProblematicSubscriptionStatus[] = [
  SubscriptionStatus.PAST_DUE,
  SubscriptionStatus.UNPAID,
];

export const INCOMPLETE_STATUSES: IncompleteSubscriptionStatus[] = [
  SubscriptionStatus.INCOMPLETE,
  SubscriptionStatus.INCOMPLETE_EXPIRED,
];

// Helper functions for subscription status checks
export const isActiveSubscription = (
  status: SubscriptionStatus
): status is ActiveSubscriptionStatus => {
  return ACTIVE_STATUSES.includes(status as ActiveSubscriptionStatus);
};

export const isInactiveSubscription = (
  status: SubscriptionStatus
): status is InactiveSubscriptionStatus => {
  return INACTIVE_STATUSES.includes(status as InactiveSubscriptionStatus);
};

export const isProblematicSubscription = (
  status: SubscriptionStatus
): status is ProblematicSubscriptionStatus => {
  return PROBLEMATIC_STATUSES.includes(status as ProblematicSubscriptionStatus);
};

export const isIncompleteSubscription = (
  status: SubscriptionStatus
): status is IncompleteSubscriptionStatus => {
  return INCOMPLETE_STATUSES.includes(status as IncompleteSubscriptionStatus);
};
